import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Link as MUILink, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import AspectRatioWrapper from "components/elements/AspectRatioWrapper";
import Card2 from "components/elements/Card2";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getColorByMode } from "utils";

interface IProps {
  id: string;
  primaryLink?: string;
  primaryHrefLink?: string;
  secondaryLink?: string;
  secondaryHrefLink?: string;
  customSecondary?: React.ReactNode;
  img?: string;
  videoId?: string;
  customBottom?: React.ReactNode;
}

type IImageBoxProps = Pick<IProps, "primaryLink" | "primaryHrefLink" | "img" | "id">;
type IVideoBoxProps = Pick<IProps, "videoId">;

const ImageBox = ({ primaryLink, primaryHrefLink, img, id }: IImageBoxProps) => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTargetBlank = primaryHrefLink?.startsWith("http");

  const content = (
    <>
      {isTargetBlank && (
        <OpenInNewIcon
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            marginLeft: isMobile ? "-16px" : "-32px",
            marginTop: isMobile ? "-16px" : "-32px",
            color: colors.white,
            fontSize: isMobile ? "32px" : "64px",
            filter: "drop-shadow(3px 3px 10px rgb(0 0 0 / 0.8))",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        />
      )}
      <img
        src={img}
        alt={t(`dashboard.communityHub.${id}.alt`)}
        style={{ width: "100%", display: "block", borderRadius: "8px" }}
      />
    </>
  );

  if (primaryHrefLink) {
    return (
      <MUILink
        href={primaryHrefLink}
        target={isTargetBlank ? "_blank" : "_self"}
        rel="noopener noreferrer"
        sx={{ display: "block", position: "relative", width: "100%" }}>
        {content}
      </MUILink>
    );
  }

  return (
    <Link to={primaryLink ?? ""} style={{ display: "block", position: "relative", width: "100%" }}>
      {content}
    </Link>
  );
};

const VideoBox = ({ videoId }: IVideoBoxProps) => (
  <AspectRatioWrapper aspectRatio={"16 / 9"}>
    <iframe
      width="100%"
      src={`https://www.youtube-nocookie.com/embed/${videoId}`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      style={{ display: "block", aspectRatio: "16 / 9", border: 0, borderRadius: "8px" }}
    />
  </AspectRatioWrapper>
);

const Card = ({
  id,
  primaryLink,
  primaryHrefLink,
  secondaryLink,
  secondaryHrefLink,
  customSecondary,
  img,
  videoId,
  customBottom,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colors } = useMUIThemeModeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();

  const handlePrimaryClick = () => {
    if (primaryHrefLink) {
      const target = primaryHrefLink.startsWith("http") ? "_blank" : "_self";
      window.open(primaryHrefLink, target);
    }

    if (primaryLink) navigate(primaryLink);
  };

  const handleSecondaryClick = () => {
    if (secondaryHrefLink) {
      const target = secondaryHrefLink.startsWith("http") ? "_blank" : "_self";
      window.open(secondaryHrefLink, target);
    }

    if (secondaryLink) navigate(secondaryLink);
  };

  return (
    <Card2 borderRadius={8}>
      <Stack spacing={3}>
        {img && <ImageBox primaryLink={primaryLink} primaryHrefLink={primaryHrefLink} img={img} id={id} />}
        {videoId && <VideoBox videoId={videoId} />}
        <Stack spacing={1.5}>
          <Typography
            fontSize={{ xs: "1rem", md: "1.25rem", lg: "1.375rem" }}
            fontWeight={fontWeight.medium}
            color={getColorByMode(theme, colors.deepMidnightBlue, colors.blueGray)}>
            {t(`dashboard.communityHub.${id}.title`)}
          </Typography>
          <Typography
            fontSize={{ xs: "0.75rem", md: "0.875rem", lg: "1rem" }}
            fontWeight={fontWeight.light}
            color={colors.blueGray}>
            <Trans i18nKey={`dashboard.communityHub.${id}.description`} />
          </Typography>
        </Stack>
        {!customBottom && (
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent={"space-between"}
            alignItems={"center"}
            spacing={{ xs: 1, lg: 0 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              onClick={handlePrimaryClick}
              endIcon={<KeyboardArrowRightIcon />}>
              {t(`dashboard.communityHub.${id}.primaryCta`)}
            </Button>
            {(secondaryHrefLink || secondaryLink) && (
              <Button
                color="primary"
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
                onClick={handleSecondaryClick}
                endIcon={<KeyboardArrowRightIcon />}>
                {t(`dashboard.communityHub.${id}.secondaryCta`)}
              </Button>
            )}
            {customSecondary}
          </Stack>
        )}
        {customBottom}
      </Stack>
    </Card2>
  );
};

export default Card;
